import { Course, ModuleConfig } from '../types';

// Helper to create a Final Exam module for a course
const createFinalExam = (courseId: string): ModuleConfig => ({
    id: `${courseId}-final-exam` as ModuleConfig['id'],
    title: 'Final Exam',
    description: 'Test Your Knowledge',
    locked: false,
    type: 'quiz'
});

export const COURSES: Course[] = [
    {
        id: 'digital-foundations',
        title: 'Course 0: Digital Citizen',
        description: 'Stay safe online and learn to think like a coder! The Explorer.',
        color: 'course-digital',
        gradient: 'from-cyan-500 to-cyan-500',
        modules: [
            { id: 'prerequisites', title: 'Backpack Check', description: 'What You Need', locked: false },
            { id: 'internet-safety', title: 'Spot the Trick', description: 'Phishing & Online Safety', locked: false },
            { id: 'digital-footprint', title: 'Your Trail', description: 'Privacy & Sharing', locked: false },
            { id: 'research-skills', title: 'Fact Checker', description: 'Finding True Info', locked: false },
            { id: 'visual-coding', title: 'Block Builder', description: 'Code Without Typing', locked: false },
            createFinalExam('digital-foundations'),
        ],
        standards: {
            iste: '1.2 Digital Citizen, 1.3 Knowledge Constructor',
            fl: 'SC.68.CS-PC.2.1, SC.68.CS-IC.1.1',
            csta: 'CSTA 1A-NI-04, 1A-IC-18',
            stel: 'STEL-7E (Technology impacts)'
        }
    },
    {
        id: 'foundation',
        title: 'Course 1: Foundation',
        description: 'Master the basics of Apps Script and APIs. The Jungle Explorer.',
        color: 'course-foundation',
        gradient: 'from-green-500 to-green-500',
        modules: [
            { id: 'architecture', title: 'The Map', description: 'Client vs Server Architecture', locked: false },
            { id: 'variables-types', title: 'Backpack Inventory', description: 'Variables & Data Types', locked: false },
            { id: 'functions-scope', title: 'The Function Oven', description: 'Functions & Scope', locked: false },
            { id: 'inventory-logic', title: 'Smart Shelf', description: 'Inventory Logic & Calculations', locked: false },
            { id: 'loops-arrays', title: 'Banana Counting', description: 'Loops & Arrays', locked: false },
            { id: 'objects-json', title: 'Parrot Talk', description: 'Objects & JSON', locked: false },
            { id: 'spreadsheet-service', title: 'The Ledger', description: 'Sheet Wizard API Builder', locked: false, path: 'sheet-wizard' },
            createFinalExam('foundation'),
        ],
        standards: {
            iste: '1.5 Computational Thinker, 1.4 Innovative Designer',
            fl: 'SC.68.CS-CP.1.1 (Algorithms)',
            csta: 'CSTA 2-AP-10, 2-AP-12',
            ccss: 'CCSS.MATH.PRACTICE.MP2 (Reason Abstractly)',
            ngss: 'HS-ETS1-2 (Design Solutions)',
            stel: 'STEL-2 (Core Tech Concepts)'
        }
    },
    {
        id: 'production',
        title: 'Course 2: Production',
        description: 'Build scalable databases with Supabase. The City Planner.',
        color: 'course-production',
        gradient: 'from-blue-500 to-blue-500',
        modules: [
            { id: 'relational-design', title: 'City Blueprints', description: 'Tables & Relationships', locked: true },
            { id: 'crud-operations', title: 'The Four Pillars', description: 'Create, Read, Update, Delete', locked: true },
            { id: 'db-migrations', title: 'Time Machine', description: 'Database Versioning', locked: true },
            { id: 'shopping-cart-db', title: 'The Market', description: 'Schema Design for E-commerce', locked: true },
            { id: 'realtime-subs', title: 'Radio Tower', description: 'Live Chat & Subscriptions', locked: true },
            createFinalExam('production'),
        ],
        standards: {
            iste: '1.4 Innovative Designer, 1.5 Computational Thinker',
            fl: 'SC.68.CS-CP.2.1 (Data Structures)',
            csta: 'CSTA 3A-DA-10',
            ngss: 'HS-ETS1-3 (Evaluate robust systems)',
            stel: 'STEL-7 (Design & Engineering)'
        }
    },
    {
        id: 'data-detective',
        title: 'Course 3: Data Detective',
        description: 'Find the truth in the data. The Librarian.',
        color: 'course-data-detective',
        gradient: 'from-amber-500 to-amber-500',
        modules: [
            { id: 'data-detective-sql', title: 'The Library', description: 'SQL SELECT Basics', locked: true },
            { id: 'filter-sort', title: 'The Sifter', description: 'WHERE & ORDER BY', locked: true },
            { id: 'sql-aggregates', title: 'The Calculator', description: 'COUNT, SUM, AVG & More', locked: true },
            { id: 'sql-joins', title: 'The Merger', description: 'JOIN Visualizer', locked: true },
            { id: 'dashboarding', title: 'The Boardroom', description: 'Chart Builder', locked: true },
            createFinalExam('data-detective'),
        ],
        standards: {
            iste: '1.3 Knowledge Constructor',
            fl: 'SC.68.CS-CP.2.4 (Data Analysis)',
            csta: 'CSTA 2-DA-08',
            ccss: 'CCSS.MATH.CONTENT.HSS.ID.A.1 (Data Representation)', 
            ngss: 'HS-ETS1-4 (Model simulations)'
        }
    },
    {
        id: 'ai-operator',
        title: 'Course 4: AI Operator',
        description: 'Tame the robots with Prompt Engineering. The Robot Tamer.',
        color: 'course-ai-operator',
        gradient: 'from-purple-500 to-purple-500',
        modules: [
            { id: 'prompt-engineering', title: 'Speaking Robot', description: 'The Prompt Lab', locked: true },
            { id: 'model-parameters', title: 'Control Panel', description: 'Temperature & Tokens', locked: true },
            { id: 'rag-basics', title: 'The Librarian', description: 'Vector Search & RAG', locked: true },
            { id: 'ai-agents', title: 'Robot Squad', description: 'Multi-Agent Collaboration', locked: true },
            createFinalExam('ai-operator'),
        ],
        standards: {
            iste: '1.1 Empowered Learner, 1.6 Creative Communicator',
            fl: 'SC.68.CS-CC.1.1 (Collaboration Tools)',
            csta: 'CSTA 3A-AP-13',
            stel: 'STEL-6 (Role of Tech in the world)'
        }
    },
    {
        id: 'cyber-security',
        title: 'Course 5: Cyber Guardian',
        description: 'Defend your kingdom from hackers. The Security Guard.',
        color: 'course-cyber-security',
        gradient: 'from-red-500 to-red-500',
        modules: [
            { id: 'cia-triad', title: 'The Shield', description: 'CIA Balance Game', locked: true },
            { id: 'password-strength', title: 'Vault Tester', description: 'Password Analysis', locked: true },
            { id: 'auth', title: 'Guardians of the Gate', description: 'Authentication Flow', locked: true },
            { id: 'ai-phishing', title: 'Deepfake Detective', description: 'Spotting AI Scams', locked: true },
            { id: 'defense-in-depth', title: 'The Castle', description: 'Layered Security Design', locked: true },
            { id: 'ai-encryption', title: 'AI vs Code', description: 'AI & Encryption', locked: true },
            { id: 'security-usability', title: 'The Balance', description: 'UX vs Security', locked: true },
            { id: 'cryptography-basics', title: 'Secret Codes', description: 'Encryption Basics', locked: true },
            { id: 'sql-injection', title: 'The Intruder', description: 'SQL Injection Lab', locked: true },
            { id: 'xss', title: 'The Graffiti', description: 'XSS Sandbox', locked: true },
            createFinalExam('cyber-security'),
        ],
        standards: {
            iste: '1.2 Digital Citizen, 1.5 Computational Thinker',
            fl: 'SC.68.CS-CS.2.4 (Cybersecurity Principles)',
            csta: 'CSTA 3B-NI-04',
            stel: 'STEL-4 (Tech Impacts & Assessment)'
        }
    },
    {
        id: 'automation',
        title: 'Course 6: Automation Architect',
        description: 'Build helpful machines that never sleep. The Inventor.',
        color: 'course-automation',
        gradient: 'from-orange-500 to-orange-500',
        modules: [
            { id: 'event-driven-arch', title: 'The Machine', description: 'Triggers & Actions', locked: true },
            { id: 'cron-scheduler', title: 'The Clock', description: 'Scheduled Jobs & Cron', locked: true },
            { id: 'webhooks', title: 'The Catcher', description: 'Webhook Game', locked: true },
            { id: 'oauth-auth', title: 'Token Exchange', description: 'OAuth 2.0 Flow', locked: true },
            createFinalExam('automation'),
        ],
        standards: {
            iste: '1.5 Computational Thinker',
            fl: 'SC.68.CS-CS.1.1 (Hardware/Software Systems)',
            csta: 'CSTA 3B-AP-14',
            ngss: 'HS-ETS1-1 (Complex real-world modeling)',
            stel: 'STEL-2 (Core Tech)'
        }
    },
    {
        id: 'ui-ux',
        title: 'Course 7: UI/UX Designer',
        description: 'Make it beautiful. The Artist.',
        color: 'course-ui-ux',
        gradient: 'from-pink-500 to-pink-500',
        modules: [
            { id: 'box-model', title: 'The Art Studio', description: 'Box Model & Layout', locked: true },
            { id: 'typography-basics', title: 'Letter Painter', description: 'Fonts & Typography', locked: true },
            { id: 'color-theory', title: 'Color Mixer', description: 'Palettes & Harmony', locked: true },
            { id: 'flexbox', title: 'Yoga Instructor', description: 'Flexbox Playground', locked: true },
            { id: 'css-grid', title: 'Grid Master', description: 'CSS Grid Builder', locked: true },
            { id: 'accessibility', title: 'Empathy Lens', description: 'Accessibility Simulator', locked: true },
            createFinalExam('ui-ux'),
        ],
        standards: {
            iste: '1.4 Innovative Designer, 1.6 Creative Communicator',
            fl: 'SC.68.CS-CP.3.1 (Program Development)',
            csta: 'CSTA 2-AP-16',
            ccss: 'CCSS.MATH.CONTENT.HSG.MG.A.3 (Geometric design)',
            stel: 'STEL-7 (Design & Innovation)'
        }
    },
    {
        id: 'sovereign-systems',
        title: 'Course 8: Sovereign Systems',
        description: 'Own your data. The King.',
        color: 'course-sovereign-systems',
        gradient: 'from-teal-500 to-teal-500',
        modules: [
            { id: 'docker-containers', title: 'The Server Rack', description: 'Containers vs VMs', locked: true },
            { id: 'server-monitoring', title: 'Mission Control', description: 'Real-time Metrics', locked: true },
            { id: 'reverse-proxies', title: 'Traffic Cop', description: 'Nginx Routing', locked: true },
            { id: 'stripe-integration', title: 'The Merchant', description: 'Payment Flow', locked: true },
            createFinalExam('sovereign-systems'),
        ],
        standards: {
            iste: '1.4 Innovative Designer',
            fl: 'SC.68.CS-CS.2.1 (Network Architectures)',
            csta: 'CSTA 3A-CS-02',
            ngss: 'HS-ETS1-3 (Evaluate competing solutions)',
            stel: 'STEL-3 (Integration of knowledge)'
        }
    },
    {
        id: 'game-dev',
        title: 'Course 9: Game Maker',
        description: 'Build your own games! The Creator.',
        color: 'course-game-dev',
        gradient: 'from-rose-500 to-rose-500',
        modules: [
            { id: 'game-loop', title: 'The Heartbeat', description: 'Frames & Animation', locked: true },
            { id: 'sprite-collision', title: 'Bump & Bounce', description: 'Collision Detection', locked: true },
            { id: 'score-system', title: 'Points & Combos', description: 'Scoring & Feedback', locked: true },
            { id: 'game-polish', title: 'Juice It!', description: 'Particles & Effects', locked: false },
            { id: 'boss-logic', title: 'The Boss', description: 'State Machines', locked: true },
            createFinalExam('game-dev'),
        ],
        standards: {
            iste: '1.5 Computational Thinker',
            fl: 'SC.68.CS-CP.3.2 (Modeling & Simulation)',
            csta: 'CSTA 2-AP-13',
            ccss: 'CCSS.MATH.CONTENT.HSA.CED.A.2 (Create equations)',
            ngss: 'HS-PS2-1 (Newtons Laws)'
        }
    },
    {
        id: 'logic-architect',
        title: 'Course 10: Logic Architect',
        description: 'Build modern responsive apps with React. The Architect.',
        color: 'course-production',
        gradient: 'from-indigo-500 to-indigo-500',
        modules: [
            { id: 'react-components', title: 'Legos of Code', description: 'Component Basics', locked: false },
            { id: 'react-state', title: 'The Brain', description: 'useState Hook', locked: false },
            { id: 'react-effects', title: 'The Spark', description: 'useEffect & Fetching', locked: false },
            { id: 'react-props', title: 'Data River', description: 'Props & Data Flow', locked: false },
            createFinalExam('logic-architect'),
        ],
        standards: {
            iste: '1.5 Computational Thinker, 1.4 Innovative Designer',
            fl: 'SC.68.CS-CP.1.2 (Programming Paradigms)',
            csta: 'CSTA 3A-AP-16',
            stel: 'STEL-7 (Design and Engineering)'
        }
    },
];

// Helper to get linear list of IDs for prerequisite checking
export const MODULE_SEQUENCE = COURSES.flatMap(c => c.modules.map(m => m.id));
